import React, { useEffect, useMemo, useState } from "react";
import { Button, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";

import SmartRtpLayout from "./components/SmartRtpLayout";
import BigPrizePosMonitor from "./components/BigPrizePosMonitor";
import { BIG_PRIZE_MONITOR_SCOPE_TABS } from "./config";
import {
  getGamesList,
  getSettingsList,
} from "../../../controllers/V1/smartRtp/smartRtpController";

const { Option } = Select;

export default function SmartRtpBigPrizeMonitorPage() {
  const [searchParams] = useSearchParams();
  const initialGameId = searchParams.get("gameId");
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [globalSettingId, setGlobalSettingId] = useState(null);
  const [scope, setScope] = useState("game");
  const [gameId, setGameId] = useState(initialGameId || null);
  const [gameSettingId, setGameSettingId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    document.title = "Crown King || Attack Mode POS Monitor";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGames = async () => {
      setGamesLoading(true);
      const res = await getGamesList({
        type: "",
        condition: { status: "A" },
        limit: 0,
        skip: 0,
        sort: { title: 1 },
      });
      if (cancelled) return;
      if (res?.status === true) {
        const list = Array.isArray(res.result) ? res.result : [];
        setGames(list);
        if (list.length && !gameId) {
          setGameId(initialGameId || list[0]._id);
        }
      } else {
        setGames([]);
      }
      setGamesLoading(false);
    };
    loadGames();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGlobalSetting = async () => {
      const res = await getSettingsList({
        type: "",
        condition: { setting_type: "Global", status: "A" },
        limit: 1,
        skip: 0,
        sort: { createdAt: -1 },
      });
      if (cancelled) return;
      if (res?.status === true && Array.isArray(res.result) && res.result[0]?._id) {
        setGlobalSettingId(res.result[0]._id);
      }
    };
    loadGlobalSetting();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (scope !== "game" || !gameId) {
      setGameSettingId(null);
      return undefined;
    }
    let cancelled = false;
    const loadGameSetting = async () => {
      const res = await getSettingsList({
        type: "",
        condition: { setting_type: "Game", game_id: gameId, status: "A" },
        limit: 1,
        skip: 0,
        sort: { createdAt: -1 },
      });
      if (cancelled) return;
      if (res?.status === true && Array.isArray(res.result) && res.result[0]?._id) {
        setGameSettingId(res.result[0]._id);
      } else {
        setGameSettingId(null);
      }
    };
    loadGameSetting();
    return () => {
      cancelled = true;
    };
  }, [scope, gameId]);

  const monitorProps = useMemo(() => {
    if (scope === "global") {
      return {
        settingId: globalSettingId,
        gameId: null,
        settingType: "Global",
      };
    }
    return {
      settingId: gameSettingId,
      gameId,
      settingType: "Game",
    };
  }, [scope, globalSettingId, gameSettingId, gameId]);

  const selectedGame = games.find((g) => String(g._id) === String(gameId));
  const scopeIndex = scope === "game" ? 0 : 1;

  const handleScopeChange = (_event, index) => {
    setScope(BIG_PRIZE_MONITOR_SCOPE_TABS[index]?.value || "game");
  };

  const addBigPrizeLink = useMemo(() => {
    if (scope === "global") {
      return "/smart-rtp/big-prize/addeditdata?scope=global";
    }
    if (gameId) {
      return `/smart-rtp/big-prize/addeditdata?gameId=${gameId}&scope=game`;
    }
    return "/smart-rtp/big-prize/addeditdata";
  }, [scope, gameId]);

  return (
    <SmartRtpLayout
      title="Attack Mode & Time Slot — POS Monitor"
      activeKey="big-prize-monitor"
      headerVariant="list"
      statusTabs={BIG_PRIZE_MONITOR_SCOPE_TABS}
      statusIndex={scopeIndex}
      onStatusChange={handleScopeChange}
      onRefresh={() => setRefreshKey((key) => key + 1)}
      actions={
        <Link to={addBigPrizeLink}>
          <Button type="primary" size="small" icon={<PlusOutlined />}>
            Bulk Upload
          </Button>
        </Link>
      }
      extraHeader={
        scope === "game" ? (
          <div className="inventory_filter_div" style={{ minWidth: 220 }}>
            <label className="filter_btn_invontry_rating_review me-1">Game</label>
            <Select
              value={gameId}
              onChange={setGameId}
              style={{ minWidth: 180 }}
              loading={gamesLoading}
              placeholder="Select game"
              showSearch
              optionFilterProp="children"
              size="small"
            >
              {games.map((game) => (
                <Option key={game._id} value={game._id}>
                  {game.title || game.game_code || game._id}
                </Option>
              ))}
            </Select>
          </div>
        ) : null
      }
    >
      {scope === "game" && selectedGame ? (
        <div className="small text-muted mb-3">
          Game code: {selectedGame.game_code || "-"}
        </div>
      ) : null}

      <div className="card shadow mb-4">
        <div className="card-body">
          <BigPrizePosMonitor key={refreshKey} standalone {...monitorProps} />
        </div>
      </div>
    </SmartRtpLayout>
  );
}
